/**
 * M10 P4b4a (plan docs/plans/2026-10-15-m10-p4b.md §3.3; D-1007's layout): operator kit parts on ship views.
 *
 * One part GLB per operator set holds every part of its kits, each authored round its `CON_` attach point in its own
 * x slot: a LOD vertex belongs to the part when `x0 ≤ x < x1`. A part instance is the set model instantiated with
 * each mesh's geometry cut to the part's slot (cut once per source geometry and slot, then shared), moved so its
 * `CON_` point is the origin, and hung on the hull at each mount: a hull socket, or an origin offset in hull asset
 * space, at the mount's uniform scale; a mirrored mount adds the port copy (x → −x). The parts wear the view's look.
 *
 * Rare (a view picks up its kit after both GLBs are in); never per frame.
 */
import { BufferAttribute, BufferGeometry, Group, Matrix4, Sphere, Vector3, type Mesh, type Object3D } from 'three/webgpu'
import type { LoadedModel, ModelInstance } from '../assets/loader'
import type { Kit, KitPart } from '../ships/kits'
import type { FactionId } from './contracts'
import { applyStaticLook, type MaterialLibrary } from './materials'

/** A kit part hung on a view: the holder on the hull and the part instance (repainted on a look change). */
export interface KitPartInstance {
  readonly holder: Object3D
  readonly instance: ModelInstance
}

const sliced = new WeakMap<BufferGeometry, Map<string, BufferGeometry>>()

/** The triangles of `geometry` whose centroid (in set asset space, through `toAsset`) lies in [x0, x1). Cached. */
export function sliceGeometry(geometry: BufferGeometry, toAsset: Matrix4, x0: number, x1: number): BufferGeometry {
  let byKey = sliced.get(geometry)
  if (byKey === undefined) {
    byKey = new Map()
    sliced.set(geometry, byKey)
  }
  const key = `${x0}:${x1}`
  const hit = byKey.get(key)
  if (hit !== undefined) return hit
  const pos = geometry.getAttribute('position')
  const index = geometry.getIndex()
  const tris = index !== null ? index.count : pos.count
  const at = (k: number): number => (index !== null ? index.getX(k) : k)
  const e = toAsset.elements
  const xOf = (v: number): number => e[0] * pos.getX(v) + e[4] * pos.getY(v) + e[8] * pos.getZ(v) + e[12]
  const keep: number[] = []
  for (let t = 0; t + 2 < tris; t += 3) {
    const a = at(t)
    const b = at(t + 1)
    const c = at(t + 2)
    const cx = (xOf(a) + xOf(b) + xOf(c)) / 3
    if (cx >= x0 && cx < x1) keep.push(a, b, c)
  }
  const out = new BufferGeometry()
  for (const [name, attr] of Object.entries(geometry.attributes)) out.setAttribute(name, attr)
  out.setIndex(new BufferAttribute(pos.count > 65535 ? new Uint32Array(keep) : new Uint16Array(keep), 1))
  out.name = `${geometry.name}:${key}`
  // Bounds from the kept vertices only (the shared attribute spans every part of the set).
  const v = new Vector3()
  const pts: Vector3[] = []
  for (let k = 0; k < keep.length; k++) pts.push(v.clone().fromBufferAttribute(pos, keep[k]))
  out.boundingSphere = pts.length > 0 ? new Sphere().setFromPoints(pts) : new Sphere()
  byKey.set(key, out)
  return out
}

/** The set model instantiated with only `part`'s slot left, its `CON_` point at the instance origin, dressed in `look`. */
function partInstance(lib: MaterialLibrary, set: LoadedModel, part: KitPart, lodScale: number, look: FactionId): ModelInstance {
  const inst = set.instantiate(lodScale)
  inst.root.position.set(0, 0, 0)
  inst.root.quaternion.identity()
  inst.root.scale.set(1, 1, 1)
  inst.root.updateMatrixWorld(true)
  const meshes: Mesh[] = []
  inst.root.traverse((o) => {
    if ((o as Mesh).isMesh) meshes.push(o as Mesh)
  })
  for (const mesh of meshes) mesh.geometry = sliceGeometry(mesh.geometry, mesh.matrixWorld, part.x0, part.x1)
  inst.root.position.set(-part.at[0], -part.at[1], -part.at[2])
  inst.root.name = `kitPart:${part.con}`
  applyStaticLook(lib, inst, look)
  return inst
}

/**
 * Hangs `kit`'s parts for hull def `hullId` on `hull` (the view's hull instance), from the loaded part set `set`.
 * Returns the hung parts (none when the kit has no mount on the hull). Rare.
 */
export function attachKitParts(lib: MaterialLibrary, hull: ModelInstance, set: LoadedModel, kit: Kit, hullId: string, lodScale: number, look: FactionId): KitPartInstance[] {
  const out: KitPartInstance[] = []
  for (const part of kit.parts) {
    const mounts = part.mounts.get(hullId)
    if (mounts === undefined) continue
    for (const m of mounts) {
      const sides = m.mirror ? [1, -1] : [1]
      for (const side of sides) {
        const instance = partInstance(lib, set, part, lodScale, look)
        const holder = new Group()
        holder.name = `kit:${kit.id}:${part.con}${side < 0 ? ':port' : ''}`
        holder.add(instance.root)
        holder.scale.set(side * m.scale, m.scale, m.scale)
        const socket = m.socket !== undefined ? hull.sockets.get(m.socket) : undefined
        if (m.socket !== undefined && socket === undefined) throw new Error(`kit ${kit.id}: socket ${m.socket} is missing on the ${hullId} model`)
        if (m.offset !== undefined) holder.position.set(side * m.offset[0], m.offset[1], m.offset[2])
        ;(socket ?? hull.root).add(holder)
        out.push({ holder, instance })
      }
    }
  }
  return out
}
